import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Inactivity policy: sign the user out after 30 minutes idle, warning 1 min before.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const WARN_BEFORE_MS = 60 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
const WARN_TOAST_ID = "idle-warning";

/**
 * Mounts inside the authenticated layout. Tracks user activity and signs the
 * user out after a period of inactivity (defense for sensitive GRC sessions).
 */
export function IdleTimer() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const warnRef = useRef(null);
  const logoutRef = useRef(null);

  useEffect(() => {
    const clearTimers = () => {
      clearTimeout(warnRef.current);
      clearTimeout(logoutRef.current);
    };

    const doLogout = async () => {
      toast.dismiss(WARN_TOAST_ID);
      await logout();
      navigate("/login");
      toast.error("You were signed out due to inactivity.");
    };

    const reset = () => {
      clearTimers();
      toast.dismiss(WARN_TOAST_ID);
      warnRef.current = setTimeout(() => {
        toast.warning("You'll be signed out in 1 minute due to inactivity.", {
          id: WARN_TOAST_ID,
          duration: WARN_BEFORE_MS,
        });
      }, IDLE_TIMEOUT_MS - WARN_BEFORE_MS);
      logoutRef.current = setTimeout(doLogout, IDLE_TIMEOUT_MS);
    };

    // Throttle activity handling to avoid resetting timers on every mousemove.
    let last = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - last > 1000) {
        last = now;
        reset();
      }
    };

    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    reset();

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
    };
  }, [logout, navigate]);

  return null;
}
